import type { CaseSummary, TreeNodeResponse } from './cors-provenance-api';
import { fetchProvenanceTreePage } from './cors-provenance-api';
import {
  LEGACY_AD_HOC_CASE_ID,
  getCurrentPersonalFilesCaseId,
  isPersonalFilesCaseId,
  personalFilesCaseIdFromUsername,
} from './ad-hoc-case';

/** Subfolder under the personal files case where manual uploads are stored. */
export const AD_HOC_MANUAL_FOLDER_NAME = 'Manual';
const MAX_MANUAL_UPLOAD_FILE_NAME_LEN = 512;

export function personalFilesCaseSummary(caseId: string): CaseSummary {
  return {
    caseName: caseId,
    caseTag: `case:${caseId.toLowerCase()}`,
    // My Files lives at data/bulk/<email>/. The folder segment is that email,
    // not a lowercased tag and not a UUID.
    folderSegment: caseId,
  };
}

/** @deprecated Synthetic leftover for the shared ``__Ad Hoc`` prefix. */
export const AD_HOC_CASE_SUMMARY: CaseSummary = personalFilesCaseSummary(LEGACY_AD_HOC_CASE_ID);

/** Provenance tree path for the personal-files Manual folder. */
export function adHocManualTreePath(caseId?: string | null): string {
  const id = (caseId?.trim() || getCurrentPersonalFilesCaseId() || LEGACY_AD_HOC_CASE_ID).trim();
  return `${id}/${AD_HOC_MANUAL_FOLDER_NAME}`;
}

export function isAdHocCase(
  caseId: string | undefined | null,
  personalFilesCaseId?: string | null,
): boolean {
  return isPersonalFilesCaseId(caseId, personalFilesCaseId);
}

function isPersonalOrLegacyCaseName(
  caseName: string,
  personalFilesCaseId?: string | null,
): boolean {
  return isPersonalFilesCaseId(caseName, personalFilesCaseId);
}

/**
 * Apply LaunchDarkly ``adHoc`` rollout policy to the cases dropdown list.
 * When disabled, strip the current user's personal files case (and leftover
 * ``__Ad Hoc``); when enabled, inject a synthetic entry if missing and keep
 * it first in the list.
 */
export function applyAdHocCaseListPolicy(
  cases: CaseSummary[],
  adHocEnabled: boolean,
  personalFilesCaseId?: string | null,
): CaseSummary[] {
  const personal = personalFilesCaseIdFromUsername(personalFilesCaseId);
  if (!adHocEnabled) {
    return cases.filter((c) => !isPersonalOrLegacyCaseName(c.caseName, personal));
  }
  const withoutLegacy =
    personal && personal.toLowerCase() !== LEGACY_AD_HOC_CASE_ID.toLowerCase()
      ? cases.filter(
          (c) => c.caseName.trim().toLowerCase() !== LEGACY_AD_HOC_CASE_ID.toLowerCase(),
        )
      : cases;
  if (!personal) return withoutLegacy;
  const summary = personalFilesCaseSummary(personal);
  const existing = withoutLegacy.find((c) => isPersonalFilesCaseId(c.caseName, personal));
  const others = withoutLegacy.filter((c) => !isPersonalFilesCaseId(c.caseName, personal));
  // Keep the list's folder spelling when it is the email. Always tag by the
  // email. A UUID tag or folder segment would filter provenance and write S3
  // under the wrong prefix for ``data/bulk/<email>/``. Other row fields (the
  // TR-2631 CSAM flag) pass through so the nav badge still sees them.
  return [
    existing
      ? {
          ...existing,
          caseTag: summary.caseTag,
          folderSegment: personalFolderSegment(existing, personal),
        }
      : summary,
    ...others,
  ];
}

function personalFolderSegment(existing: CaseSummary, personal: string): string {
  const fromList = existing.folderSegment?.trim();
  if (fromList && fromList.toLowerCase() === personal.toLowerCase()) return fromList;
  const fromName = existing.caseName?.trim();
  if (fromName && fromName.toLowerCase() === personal.toLowerCase()) return fromName;
  return personal;
}

function pathIdentityMatches(
  segmentsBeforeManual: string[],
  caseId: string | undefined | null,
): boolean {
  const identities = new Set<string>();
  identities.add(LEGACY_AD_HOC_CASE_ID.toLowerCase());
  const personal =
    personalFilesCaseIdFromUsername(caseId) ?? getCurrentPersonalFilesCaseId();
  if (personal) identities.add(personal.toLowerCase());
  if (caseId?.trim()) identities.add(caseId.trim().toLowerCase());
  return segmentsBeforeManual.some((s) => identities.has(s));
}

/** True when a tree file row lives under ``…/<personal-files>/Manual/…``. */
export function isAdHocManualTreeFile(
  node: TreeNodeResponse,
  caseId: string | undefined | null,
  personalFilesCaseId?: string | null,
): boolean {
  if (!isAdHocCase(caseId, personalFilesCaseId) || node.nodeType === 'directory') return false;
  const segments = node.path
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const manualIdx = segments.lastIndexOf(AD_HOC_MANUAL_FOLDER_NAME.toLowerCase());
  if (manualIdx < 0) return false;
  return pathIdentityMatches(segments.slice(0, manualIdx), caseId);
}

/** Match a provenance path/URL whose basename is ``fileName`` under ``<case>/Manual``. */
export function matchesAdHocManualFileName(
  pathOrUrl: string,
  fileName: string,
  caseId?: string | null,
): boolean {
  const trimmedName = fileName.trim();
  if (!trimmedName) return false;
  const segments = pathOrUrl
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const manualIdx = segments.findIndex(
    (s) => s.toLowerCase() === AD_HOC_MANUAL_FOLDER_NAME.toLowerCase(),
  );
  if (manualIdx < 0) return false;
  if (segments[segments.length - 1] !== trimmedName) return false;
  return pathIdentityMatches(
    segments.slice(0, manualIdx).map((s) => s.toLowerCase()),
    caseId,
  );
}

/** Validate a manual upload basename (mirrors edge-lambda ``file_upload`` rules). */
export function validateAdHocManualUploadFileName(
  fileName: string,
  existingNames: ReadonlySet<string>,
): string | null {
  const name = fileName.trim();
  if (!name) return 'File name is required.';
  if (name.length > MAX_MANUAL_UPLOAD_FILE_NAME_LEN) return 'File name is too long.';
  if (name.includes('/') || name.includes('\\') || name.includes('..')) {
    return 'File name cannot contain path separators.';
  }
  if (existingNames.has(name)) {
    return `A file named "${name}" already exists in Manual.`;
  }
  return null;
}

/**
 * Validate a rename during an upload conflict prompt. The conflicting basename must
 * always count as taken (even when ``existingNames`` omitted it, e.g. batch dupes).
 */
export function validateAdHocManualUploadRename(
  newFileName: string,
  conflictingFileName: string,
  existingNames: ReadonlySet<string>,
): string | null {
  const trimmed = newFileName.trim();
  const conflicting = conflictingFileName.trim();
  if (trimmed === conflicting) {
    return `Choose a different name than "${conflicting}".`;
  }
  const taken = new Set(existingNames);
  taken.add(conflicting);
  return validateAdHocManualUploadFileName(trimmed, taken);
}

/** Suggest a non-conflicting rename when a basename is taken. */
export function suggestAdHocManualUploadRename(
  fileName: string,
  existingNames: ReadonlySet<string>,
): string {
  const trimmed = fileName.trim();
  const dot = trimmed.lastIndexOf('.');
  const stem = dot > 0 ? trimmed.slice(0, dot) : trimmed;
  const ext = dot > 0 ? trimmed.slice(dot) : '';

  for (let i = 1; i < 100; i++) {
    const candidate = `${stem} (${i})${ext}`;
    if (!existingNames.has(candidate)) return candidate;
  }
  return `${stem} (${Date.now()})${ext}`;
}

/** List basenames currently stored under the personal-files Manual folder. */
export async function fetchAdHocManualFileNames(args: {
  cellId: string;
  accessToken: string;
  caseId?: string | null;
  signal?: AbortSignal;
}): Promise<Set<string>> {
  const caseId = args.caseId?.trim() || getCurrentPersonalFilesCaseId();
  if (!caseId) return new Set();

  const names = new Set<string>();
  let offset = 0;
  const limit = 200;
  const tags = [`case:${caseId.toLowerCase()}`];

  while (true) {
    const page = await fetchProvenanceTreePage({
      cellId: args.cellId,
      accessToken: args.accessToken,
      path: adHocManualTreePath(caseId),
      view: 'user',
      tags,
      tagMatch: 'any',
      offset,
      limit,
      signal: args.signal,
    });

    for (const node of page.items) {
      if (node.nodeType !== 'file') continue;
      const name = node.name.trim();
      if (name) names.add(name);
    }

    if (page.nextOffset === null) break;
    offset = page.nextOffset;
  }

  return names;
}
